import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';

import { useShouldEnableMenuEditor } from '@provider-portal/common/experiments/providerOnboarding';
import { useIsRoutePartOf } from '@provider-portal/common/routing/hooks';
import {
  defaultRoute,
  menuEditorRoutePrefix,
  onboardingAddNewMerchantVenueRoute,
  onboardingRoutePrefix,
  settingsRoutePrefix,
} from '@provider-portal/common/routing/routes';
import { AccountStage } from '@provider-portal/common/services/types/account';

import {
  selectAccountStageOrNewMerchantVenueOnboarding,
  selectIsSelectedProviderNewMerchantVenueInOnboarding,
} from 'rdx/account';

function useAccessControlRedirectRoute(): null | string {
  const accountStage = useSelector(selectAccountStageOrNewMerchantVenueOnboarding);
  const isOnboardingRoute = useIsRoutePartOf(onboardingRoutePrefix);
  const isMenuEditorRoute = useIsRoutePartOf(menuEditorRoutePrefix);
  const isSettingsRoute = useIsRoutePartOf(settingsRoutePrefix);
  const isOnboardingNMVRoute = useIsRoutePartOf(onboardingAddNewMerchantVenueRoute);
  const shouldEnableMenuEditorForSSU = useShouldEnableMenuEditor();
  const isNewMerchantVenueOnboarding = useSelector(
    selectIsSelectedProviderNewMerchantVenueInOnboarding,
  );
  const shouldEnableMerchantMenuEditor =
    shouldEnableMenuEditorForSSU || isNewMerchantVenueOnboarding;

  switch (accountStage) {
    case AccountStage.ONBOARDING:
      if (isOnboardingRoute) {
        if (isOnboardingNMVRoute) {
          return onboardingRoutePrefix;
        }
        return null;
      }

      if (isMenuEditorRoute && shouldEnableMerchantMenuEditor) {
        return null;
      }

      if (isSettingsRoute) {
        return null;
      }

      return onboardingRoutePrefix;

    case AccountStage.ACTIVATED:
      if (!isOnboardingRoute) {
        return null;
      }

      // @todo move NVM route outside onboarding prefix
      if (isOnboardingNMVRoute) {
        return null;
      }

      return defaultRoute;

    default:
      return null;
  }
}

export function AccessControl({ children }: { children: React.ReactNode }) {
  const accessControlRedirectRoute = useAccessControlRedirectRoute();

  if (accessControlRedirectRoute !== null) {
    return <Redirect to={accessControlRedirectRoute} />;
  }

  return <>{children}</>;
}
